import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Filter, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { API } from './adminConstants';

const STAGE_LABELS = {
  sent: { label: 'Recebeu mensagem', cls: 'bg-slate-100 text-slate-600' },
  opened: { label: 'Abriu a página', cls: 'bg-amber-100 text-amber-700' },
  confirmed: { label: 'Deu o OK ✅', cls: 'bg-emerald-100 text-emerald-700' },
};

const MILESTONE_LABELS = {
  trial_d2: 'Trial D-2', trial_d0: 'Trial D0', trial_d7: 'Trial D+7',
  free_30d: 'Free 30d', free_60d: 'Free 60d',
};

const AdminRecoveryFunnel = () => {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);

  const fetchFunnel = useCallback(() => {
    axios.get(`${API}/admin/recovery-funnel`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => setData(r.data)).catch(() => setData({ sent: 0, opened: 0, confirmed: 0, items: [] }));
  }, [getToken]);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');

  return (
    <Card data-testid="recovery-funnel-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Filter className="w-5 h-5 text-[#C5A059]" /> Funil de Recuperação (OK de 1 Toque)</CardTitle>
        <p className="text-sm text-slate-500">Clientes free/trial que receberam o link do OK. Quem abriu e não confirmou é o alvo ideal do follow-up humano da equipe.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#C5A059]" /></div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center" data-testid="funnel-stats">
              <div className="bg-slate-50 rounded-lg py-3">
                <p className="text-2xl font-bold text-[#0A2647]">{data.sent}</p>
                <p className="text-[10px] text-slate-400 uppercase">Receberam</p>
              </div>
              <div className="bg-amber-50 rounded-lg py-3">
                <p className="text-2xl font-bold text-amber-600">{data.opened}</p>
                <p className="text-[10px] text-slate-400 uppercase">Abriram ({pct(data.opened, data.sent)})</p>
              </div>
              <div className="bg-emerald-50 rounded-lg py-3">
                <p className="text-2xl font-bold text-emerald-600">{data.confirmed}</p>
                <p className="text-[10px] text-slate-400 uppercase">Deram OK ({pct(data.confirmed, data.sent)})</p>
              </div>
            </div>
            {data.items.length > 0 && (
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="funnel-list">
                {data.items.map(i => (
                  <div key={i.id} className="py-2.5 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0A2647] truncate">{i.name}</p>
                      <p className="text-[11px] text-slate-400">
                        {(i.milestones || []).map(m => MILESTONE_LABELS[m] || m).join(' · ') || '—'}
                        {i.confirmed_plan ? ` · fechou ${i.confirmed_plan}` : ''}
                      </p>
                    </div>
                    <span className={`flex-shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full ${STAGE_LABELS[i.stage].cls}`}>
                      {STAGE_LABELS[i.stage].label}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {data.items.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-2">Nenhuma mensagem de recuperação enviada ainda — o funil enche sozinho conforme os lembretes automáticos saem.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminRecoveryFunnel;
